<template>
  <SelectionLinkMenu
    :title="$t('page.analysis.text-selection.link-this-annotation')"
    :emptyMessage="$t('page.analysis.text-selection.no-items')"
    :items="filteredItems"
    :horizontalPosition="horizontalPosition"
    :verticalPosition="verticalPosition"
    :searchAllowed="searchAllowed"
    :submenuTitle="$t('page.analysis.text-selection.submenu-choose-item')"
    :showItemButtons="isAdmin"
    :isItemConnected="checkIsConnected"
    :isSubmenuItemConnected="checkIsSubmenuItemConnected"
    :isSubmenuItemDisabled="checkIsSubmenuItemUsed"
    :submenuItemDisabledTooltip="$t('page.analysis.text-selection.submenu-item-taken')"
    :displayedButton="computeDisplayedButton"
    @select-item="onItemSelect"
    @select-item-button="onItemButtonSelect"
    @select-submenu-item="onSubmenuItemSelect"
    @search="filterGridItems"
    @size-changed="$emit('sizeChanged', $event)"
    @close="$emit('close', true)"
    @mouseenter.native="$emit('mouseenter')"
  >
    <template #item="{ item }">{{ item.label }}</template>
    <template #button-label="{ button }">{{ $t(`page.analysis.text-selection.${button.name}`) }}</template>
  </SelectionLinkMenu>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isAdminTenant } from '@/models/role';
import AnnotationHighlightMixin from '@/mixins/annotation-highlight-mixin';
import SelectionLinkMenu from '@/components/pdf-viewer/SelectionLinkMenu.vue';

export default {
  name: 'EvaluationLinkMenu',
  components: { SelectionLinkMenu },
  mixins: [AnnotationHighlightMixin],
  props: {
    selection: Object,
    searchAllowed: {
      type: Boolean,
      default: false,
    },
    horizontalPosition: {
      type: Object,
    },
    verticalPosition: {
      type: Object,
    },
    opportunityId: String,
  },
  data() {
    return {
      gridItems: [],
      filteredItems: [],
    };
  },
  created() {
    // Keep same order as store (pinned first, then field.order) — do not re-sort by name.
    this.gridItems = this.evaluationFields.slice();
    this.filteredItems = this.gridItems.map((item) => this.toGenericItem(item));
  },
  computed: {
    ...mapGetters('evaluationGrid', ['evaluationFields']),
    ...mapGetters('annotations', ['linkedAnnotations']),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('opportunityDocuments', ['opportunityDocument']),
    isAdmin() {
      return isAdminTenant(this.currentUser.role);
    },
  },
  methods: {
    ...mapActions('evaluationGrid', ['addEvalGridValue']),
    toGenericItem(field) {
      return {
        id: field.id,
        label: field.name,
        choices: field.choices,
        buttons: field.buttons,
        values: field.values,
        order: field.order,
        _raw: field,
      };
    },
    checkIsConnected(item) {
      if (!this.selectedAnnotation) return false;
      return !!this.linkedAnnotations.find((annotation) => {
        return (
          annotation.values.find((value) => value.evaluationGridFieldId === item.id) &&
          this.selectedAnnotation &&
          annotation.textAnnotationId === this.selectedAnnotation.id
        );
      });
    },
    checkIsSubmenuItemConnected(item, choice) {
      if (!this.selectedAnnotation) return false;
      const field = this.evaluationFields.find((f) => f.id === item.id);
      if (!field) return false;
      const value = field.values.find((v) => v.value === choice && v.textAnnotationId === this.selectedAnnotation.id);
      return !!value;
    },
    checkIsSubmenuItemUsed(item, choice) {
      const field = this.evaluationFields.find((f) => f.id === item.id);
      if (!field) return false;
      const value = field.values.find((v) => {
        if (!this.selectedAnnotation) {
          return v.value === choice;
        } else {
          return v.value === choice && v.textAnnotationId !== this.selectedAnnotation.id;
        }
      });
      return !!value;
    },
    computeDisplayedButton(item, button) {
      const isKey = (value) => value.valueType === 'KEY';
      const isValue = (value) => value.valueType === 'VALUE';

      const keyButton = item.buttons.find((btn) => btn.name === 'key');
      const valueButton = item.buttons.find((btn) => btn.name === 'value');

      item.buttons.forEach((btn) => (btn.selected = false));

      const pdfAnnotationValues = item.values.filter((value) => value.source === 'PDF_ANNOTATION');
      pdfAnnotationValues.forEach((value) => {
        if (this.selectedAnnotation && value.textAnnotationId === this.selectedAnnotation.id) {
          keyButton.selected = isKey(value);
          valueButton.selected = isValue(value);
        }
      });

      return button;
    },
    filterGridItems(query) {
      const filtered = this.gridItems.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
      this.filteredItems = filtered.map((item) => this.toGenericItem(item));
    },
    async onItemSelect(item) {
      let highlightAnnotation = false;
      let itemToLink;
      let matchedItem;

      if (!this.selectedAnnotation) {
        await this.highlightAnnotationAndReturnSelected();
        highlightAnnotation = true;
      }

      this.gridItems.forEach((gridItem) => {
        gridItem.values.forEach((gridValue) => {
          const matchingItem = item.values.find(
            (itemValue) =>
              itemValue.id === gridValue.id &&
              itemValue.source === 'PDF_ANNOTATION' &&
              itemValue.textAnnotationId === this.selectedAnnotation.id,
          );
          if (matchingItem) {
            matchedItem = matchingItem;
          }
        });
      });

      itemToLink = { ...item, valueType: 'VALUE' };

      if (matchedItem) {
        if (itemToLink.values.length === 0) {
          this.$emit('linkSelectionToItem', itemToLink);
        } else if (
          itemToLink.values.some((value) => value.evaluationGridFieldId === matchedItem.evaluationGridFieldId)
        ) {
          if (matchedItem.valueType !== 'VALUE') {
            matchedItem.valueType = 'VALUE';
            this.$emit('update', matchedItem);
          }
        } else {
          this.$emit('linkSelectionToItem', itemToLink);
        }
      } else {
        highlightAnnotation ? this.addLinkSelectionToItem(itemToLink) : this.$emit('linkSelectionToItem', itemToLink);
      }
      this.$emit('close', true);
    },
    async onItemButtonSelect(item, selectedButton) {
      let highlightAnnotation = false;
      let itemToLink;
      let matchedItem;
      let selectedButtonType;

      if (!this.selectedAnnotation) {
        await this.highlightAnnotationAndReturnSelected();
        highlightAnnotation = true;
      }

      this.gridItems.forEach((gridItem) => {
        gridItem.values.forEach((gridValue) => {
          const matchingItem = item.values.find(
            (itemValue) =>
              itemValue.id === gridValue.id &&
              itemValue.source === 'PDF_ANNOTATION' &&
              itemValue.textAnnotationId === this.selectedAnnotation.id,
          );
          if (matchingItem) {
            matchedItem = matchingItem;
          }
        });
      });

      selectedButtonType = selectedButton.name;
      itemToLink = { ...item, valueType: selectedButtonType.toUpperCase() };

      if (matchedItem) {
        if (itemToLink.values.length === 0) {
          this.$emit('linkSelectionToItem', itemToLink);
        } else if (
          itemToLink.values.some((value) => value.evaluationGridFieldId === matchedItem.evaluationGridFieldId)
        ) {
          if (matchedItem.valueType !== selectedButtonType.toUpperCase()) {
            matchedItem.valueType = selectedButtonType.toUpperCase();
            this.$emit('update', matchedItem);
          }
        } else {
          this.$emit('linkSelectionToItem', itemToLink);
        }
      } else {
        highlightAnnotation ? this.addLinkSelectionToItem(itemToLink) : this.$emit('linkSelectionToItem', itemToLink);
      }
      this.$emit('close', true);
    },
    async onSubmenuItemSelect(item, choice) {
      let highlightAnnotation = false;
      const itemToLink = {
        id: item.id,
        valueType: 'VALUE',
        value: choice,
      };

      if (!this.selectedAnnotation) {
        await this.highlightAnnotationAndReturnSelected();
        highlightAnnotation = true;
      }

      highlightAnnotation
        ? this.addLinkSelectionToSelectItem(itemToLink)
        : this.$emit('linkSelectionToSelectItem', itemToLink);

      this.$emit('close', true);
    },
    addLinkSelectionToItem(item) {
      this.addEvalGridValue({
        opportunityId: this.opportunityId,
        fieldId: item.id,
        source: 'PDF_ANNOTATION',
        textAnnotationId: this.selectedAnnotation.id,
        opportunityDocumentId: this.opportunityDocument.id,
        value: this.selectedAnnotation.text,
        valueType: item.valueType,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.$emit('close', true);
        })
        .catch(() => {
          this.resetSelectedAnnotation();
        });
    },
    addLinkSelectionToSelectItem(item) {
      this.addEvalGridValue({
        opportunityId: this.opportunityId,
        fieldId: item.id,
        source: 'PDF_ANNOTATION',
        textAnnotationId: this.selectedAnnotation.id,
        opportunityDocumentId: this.opportunityDocument.id,
        value: item.value,
        valueType: item.valueType,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.$emit('close', true);
        })
        .catch(() => {
          this.resetSelectedAnnotation();
        });
    },
  },
};
</script>
